import { resolveGatewayName } from '@payments/shared/utils/roundPayments';
import { IN_APP_BROWSER_UA } from '../common/constants/inAppBrowser';

function roundNumber(number: number | null, numberOfDigits = 2): number | null {
  if (number === null) {
    return null;
  }

  return Number(number.toFixed(numberOfDigits));
}

async function getHashedText(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function useHelpers() {
  const { GATEWAY_NAME, PAYPAL_GATEWAY_NAME } = useRuntimeConfig().public;
  const isInputFocused = ref(false);
  const device = useDevice();
  const gatewayStore = useKegelGatewayStore();

  function onFocusInput() {
    if (device.isAndroid) {
      isInputFocused.value = true;
    }
  }

  const gatewayType = computed(() => {
    return resolveGatewayName(gatewayStore.provider, gatewayStore.lagoCardProvider, gatewayStore.gn || GATEWAY_NAME);
  });

  const paypalGatewayType = computed(() => {
    return gatewayStore.pgn || PAYPAL_GATEWAY_NAME || 'Paypal';
  });

  const isTikTokInApp = computed((): boolean => {
    if (!import.meta.client) {
      return false;
    }

    return IN_APP_BROWSER_UA.TIKTOK.test((navigator.userAgent || '').toLowerCase());
  });

  const isSnapchatInApp = computed((): boolean => {
    if (!import.meta.client) {
      return false;
    }

    const ua = (navigator.userAgent || '').toLowerCase();
    const hasSnapchatMarkers = IN_APP_BROWSER_UA.SNAPCHAT.test(ua);
    const hasSnapKitMarkers = IN_APP_BROWSER_UA.SNAP_KIT.test(ua);
    const isAndroidWV = IN_APP_BROWSER_UA.ANDROID.test(ua)
      && IN_APP_BROWSER_UA.ANDROID_WEBVIEW.test(ua);
    const hasGenericSnapWV = isAndroidWV && IN_APP_BROWSER_UA.SNAP.test(ua);

    return hasSnapchatMarkers || hasSnapKitMarkers || hasGenericSnapWV;
  });

  return {
    roundNumber,
    gatewayType,
    paypalGatewayType,
    onFocusInput,
    isInputFocused,
    isTikTokInApp,
    getHashedText,
    isSnapchatInApp,
  };
}
